import { Component, EventEmitter, OnInit, Output, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormControl } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { PlayModelOptions } from "../../../../../controller/video-scene.service";
import { MatIconButton } from "@angular/material/button";
import { Subject } from 'rxjs';
import { TeamOptionResponse } from "@solverml/api";
import { PLAY_MODEL_LABEL } from "../../scene-table/play-model-reference";


export interface FilterChangeEvent {
  ids: number[];
  teamId: string | null;
  playModelQuery: string;
}


@Component({
  selector: 'app-scene-filter',
  templateUrl: './scene-filter.component.html',
  styleUrls: ['./scene-filter.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    MatIconButton
  ]
})
export class SceneFilterComponent implements OnInit, OnDestroy {

  @Output() onFilterChange = new EventEmitter<FilterChangeEvent>();
  @Input() teams: TeamOptionResponse[] = [];
  private _playModelOptions: PlayModelOptions = {};
  private destroy$ = new Subject<void>();

  readonly playModelLabel = PLAY_MODEL_LABEL;

  @Input()
  set playModelOptions(options: PlayModelOptions) {
    if (options && Object.keys(options).length > 0) {
      this._playModelOptions = options;

      this.filteredKeys = Object.keys(this._playModelOptions)
        .filter(displayKey => !this.ignoredKeys.includes(displayKey) && displayKey !== this.playModelLabel)
        .map(displayKey => {
          return {
            clean: displayKey.replace(/[^a-zA-Z0-9]/g, ''),
            display: displayKey
          };
        });

      this.buildForm();
      this.isLoading = false;
    }
  }

  get playModelOptions(): PlayModelOptions {
    return this._playModelOptions;
  }

  filterForm: FormGroup = this.fb.group({});

  public filteredKeys: { clean: string, display: string }[] = [];

  private ignoredKeys = ['Asistencia', 'Goleador', 'Tiempo'];
  isLoading = true;

  constructor(
    private fb: FormBuilder
  ) {}

  ngOnInit() {
    // El 'setter' se encarga de todo.
  }

  private buildForm() {
    const formControls: Record<string, any> = {};

    formControls['attackingTeam'] = this.fb.control(null);
    formControls['playModelQuery'] = this.fb.control('');

    for (const key of this.filteredKeys) {
      formControls[key.clean] = this.fb.control([]);
    }

    this.filterForm = this.fb.group(formControls);

    this.destroy$.next();
    this.destroy$.complete();
    this.destroy$ = new Subject<void>();

    this.filterForm.valueChanges.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(formValue => {
      const teamId = formValue['attackingTeam'] || null;
      const playModelQuery = (formValue['playModelQuery'] || '').toString().trim();

      const ids: number[] = [];
      Object.keys(formValue).forEach(key => {
        if (key !== 'attackingTeam' && key !== 'playModelQuery' && Array.isArray(formValue[key])) {
          ids.push(...formValue[key]);
        }
      });

      setTimeout(() => {
        this.onFilterChange.emit({ ids, teamId, playModelQuery });
      }, 0);
    });
  }

  clearSelection(controlName: string, event: MouseEvent) {
    event.stopPropagation();
    const val = controlName === 'attackingTeam' ? null : controlName === 'playModelQuery' ? '' : [];
    this.filterForm.get(controlName)?.setValue(val);
  }

  public resetForm(): void {
    this.filterForm.reset({});
  }

  public removeFilterById(idToRemove: number): void {
    if (!this._playModelOptions) return;

    let targetControlName: string | null = null;

    for (const key of this.filteredKeys) {
      const options = this._playModelOptions[key.display];
      if (Array.isArray(options)) {
        const foundOption = options.find(opt => opt.id === idToRemove);
        if (foundOption) {
          targetControlName = key.clean;
          break;
        }
      }
    }

    if (targetControlName) {
      const control = this.filterForm.get(targetControlName) as FormControl;
      if (control) {
        const currentValues: number[] = control.value || [];
        const newValues = currentValues.filter(id => id !== idToRemove);

        control.setValue(newValues, { emitEvent: false });

        const formValue = this.filterForm.value;
        const currentTeamId = formValue['attackingTeam'] || null;
        const currentPlayModelQuery = (formValue['playModelQuery'] || '').toString().trim();

        const allSelectedIds = Object.keys(formValue)
          .filter(k => k !== 'attackingTeam' && k !== 'playModelQuery')
          .map(k => formValue[k])
          .flat()
          .filter(id => id !== null) as number[];

        setTimeout(() => {
          this.onFilterChange.emit({
            ids: allSelectedIds,
            teamId: currentTeamId,
            playModelQuery: currentPlayModelQuery
          });
        }, 0);
      }
    } else {
      console.warn(`No se encontró la categoría para el ID: ${idToRemove}`);
    }
  }

  public removeTeamFilter(): void {
    const control = this.filterForm.get('attackingTeam');
    if (control) {
      control.setValue(null, { emitEvent: false });

      const formValue = this.filterForm.value;
      const allSelectedIds = Object.keys(formValue)
        .filter(k => k !== 'attackingTeam' && k !== 'playModelQuery')
        .map(k => formValue[k])
        .flat()
        .filter(id => id !== null) as number[];

      this.onFilterChange.emit({
        ids: allSelectedIds,
        teamId: null,
        playModelQuery: (formValue['playModelQuery'] || '').toString().trim()
      });
    }
  }

  public removePlayModelQuery(): void {
    const control = this.filterForm.get('playModelQuery');
    if (control) {
      control.setValue('', { emitEvent: false });

      const formValue = this.filterForm.value;
      const allSelectedIds = Object.keys(formValue)
        .filter(k => k !== 'attackingTeam' && k !== 'playModelQuery')
        .map(k => formValue[k])
        .flat()
        .filter(id => id !== null) as number[];

      this.onFilterChange.emit({
        ids: allSelectedIds,
        teamId: formValue['attackingTeam'] || null,
        playModelQuery: ''
      });
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
